import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"
import { Button } from "@/components/ui/button"
import AppLayout from "@/layouts/AppLayout"
import { Head, useForm, usePage } from "@inertiajs/react"
import { IconArrowLeft } from "@tabler/icons-react"
import LegalDocumentForm, {
  type CustomerOption,
  type FormValues,
  type Option,
  type ParentOption,
} from "./components/LegalDocumentForm"

interface Props {
  [key: string]: unknown
  document: { customer_id: number | null }
  customers: CustomerOption[]
  document_types: Option[]
  statuses: Option[]
  currencies: string[]
  parent_options: ParentOption[]
}

export default function LegalDocumentNew() {
  const { document, customers, document_types, statuses, currencies, parent_options } = usePage<Props>().props

  const { data, setData, post, processing, errors } = useForm<FormValues>({
    customer_id: document.customer_id ? String(document.customer_id) : "",
    parent_document_id: "",
    document_type: "msa",
    status: "draft",
    title: "",
    effective_date: "",
    execution_date: "",
    end_date: "",
    governing_law: "",
    currency: "",
    document_url: "",
    notes: "",
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    post("/legal_documents")
  }

  const cancelHref = document.customer_id ? `/customers/${document.customer_id}` : "/legal_documents"

  return (
    <>
      <Head title="New Document - Operations Center" />
      <div className="flex flex-1 flex-col gap-6 p-4 lg:p-6">
        <div className="space-y-4">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href="/">Home</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink href="/legal_documents">Documents</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>New</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="h-8 w-8" asChild>
              <a href={cancelHref}>
                <IconArrowLeft className="h-4 w-4" />
              </a>
            </Button>
            <h1 className="text-lg font-semibold tracking-tight">New Document</h1>
          </div>
        </div>

        <div className="max-w-3xl">
          <LegalDocumentForm
            values={data}
            errors={errors}
            customers={customers}
            documentTypes={document_types}
            statuses={statuses}
            currencies={currencies}
            parentOptions={parent_options}
            processing={processing}
            submitLabel="Create Document"
            cancelHref={cancelHref}
            onChange={(field, value) => setData(field, value)}
            onSubmit={handleSubmit}
          />
        </div>
      </div>
    </>
  )
}

LegalDocumentNew.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
